import PropTypes from 'prop-types';
import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en';
import Select from 'react-select';

const countryOptions = getCountries().map((country) => ({
  value: `+${getCountryCallingCode(country)}`,
  label: en[country],
  code: country,
}));

const urlTypeOptions = [
  { value: 'Static', label: 'Static' },
  { value: 'Dynamic', label: 'Dynamic' },
];

const ButtonTypeControl = ({ button, index, template, onButtonChange, onRemoveButton }) => {
  const validatePhoneNumber = (number) => {
    if (!number) return '';
    return number.replace(/[^\d+]/g, '');
  };

  const cleanButtonDataByType = (type, currentData = {}) => {
    const baseData = {
      type,
      text: currentData.text || '',
    };

    switch (type) {
      case 'URL':
        return {
          ...baseData,
          url: '',
          urlType: 'Static',
          phoneNumber: undefined,
          countryCode: undefined,
          offerCode: undefined,
          footerText: undefined,
          optOutAcknowledged: undefined,
        };
      case 'PHONE_NUMBER':
        return {
          ...baseData,
          phoneNumber: '',
          countryCode: '+1',
          url: undefined,
          urlType: undefined,
          offerCode: undefined,
          footerText: undefined,
          optOutAcknowledged: undefined,
        };
      case 'QUICK_REPLY':
        if (
          currentData.text &&
          currentData.text.toLowerCase().includes('stop') &&
          (currentData.text.toLowerCase().includes('promotion') ||
            currentData.text.toLowerCase().includes('marketing'))
        ) {
          return {
            ...baseData,
            footerText: currentData.footerText || 'Not interested? Tap Stop promotions',
            optOutAcknowledged: currentData.optOutAcknowledged || false,
            url: undefined,
            urlType: undefined,
            phoneNumber: undefined,
            countryCode: undefined,
            offerCode: undefined,
          };
        } else if (
          currentData.text &&
          currentData.text.toLowerCase().includes('copy') &&
          currentData.text.toLowerCase().includes('offer')
        ) {
          return {
            ...baseData,
            offerCode: currentData.offerCode || '',
            url: undefined,
            urlType: undefined,
            phoneNumber: undefined,
            countryCode: undefined,
            footerText: undefined,
            optOutAcknowledged: undefined,
          };
        }
        // Regular Quick Reply
        return {
          ...baseData,
          url: undefined,
          urlType: undefined,
          phoneNumber: undefined,
          countryCode: undefined,
          offerCode: undefined,
          footerText: undefined,
          optOutAcknowledged: undefined,
        };
      default:
        return baseData;
    }
  };

  const isCopyOfferCodeButton = () => {
    if (button.offerCode !== undefined) {
      return true;
    }

    return (
      button.type === 'QUICK_REPLY' &&
      button.text &&
      button.text.toLowerCase().includes('copy') &&
      button.text.toLowerCase().includes('offer')
    );
  };

  const isMarketingOptOutButton = () => {
    if (button.optOutAcknowledged !== undefined) {
      return true;
    }

    return (
      button.type === 'QUICK_REPLY' &&
      button.text &&
      button.text.toLowerCase().includes('stop') &&
      (button.text.toLowerCase().includes('promotion') ||
        button.text.toLowerCase().includes('marketing'))
    );
  };

  const getButtonTypeOptions = () => {
    const options = [
      { value: 'QUICK_REPLY', label: 'Quick Reply' },
      { value: 'URL', label: 'Visit Website' },
      { value: 'PHONE_NUMBER', label: 'Call Phone Number' },
    ];

    if (template.category === 'MARKETING') {
      options.push({ value: 'COPY_OFFER_CODE', label: 'Copy Offer Code' });
      options.push({ value: 'MARKETING_OPT_OUT', label: 'Marketing Opt-Out' });
    }

    return options;
  };

  const getCurrentButtonType = () => {
    if (isCopyOfferCodeButton()) {
      return { value: 'COPY_OFFER_CODE', label: 'Copy Offer Code' };
    }

    if (isMarketingOptOutButton()) {
      return { value: 'MARKETING_OPT_OUT', label: 'Marketing Opt-Out' };
    }

    // Standard button types
    switch (button.type) {
      case 'QUICK_REPLY':
        return { value: 'QUICK_REPLY', label: 'Quick Reply' };
      case 'URL':
        return { value: 'URL', label: 'Visit Website' };
      case 'PHONE_NUMBER':
        return { value: 'PHONE_NUMBER', label: 'Call Phone Number' };
      default:
        return { value: button.type, label: button.type };
    }
  };

  const handleButtonTypeChange = (values) => {
    if (!values || values.length === 0) return;

    const selectedValue = values[0].value;

    if (selectedValue === 'COPY_OFFER_CODE') {
      onButtonChange(index, {
        type: 'QUICK_REPLY',
        text: 'Copy offer code',
        offerCode: '',
      });
    } else if (selectedValue === 'MARKETING_OPT_OUT') {
      onButtonChange(index, {
        type: 'QUICK_REPLY',
        text: 'Stop promotions',
        footerText: 'Not interested? Tap Stop promotions',
        optOutAcknowledged: false,
      });
    } else {
      let defaultText = '';
      switch (selectedValue) {
        case 'QUICK_REPLY':
          defaultText = 'Reply';
          break;
        case 'URL':
          defaultText = 'Visit website';
          break;
        case 'PHONE_NUMBER':
          defaultText = 'Call us';
          break;
        default:
          defaultText = '';
      }

      const cleanedData = cleanButtonDataByType(selectedValue, {
        text: defaultText,
      });
      onButtonChange(index, cleanedData);
    }
  };

  const renderButtonFields = () => {
    if (button.type === 'PHONE_NUMBER') {
      return (
        <Row className="align-items-start">
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Type of Action</Form.Label>
              <div className="wa-button-select-wrapper">
                <Select
                  value={getCurrentButtonType()}
                  onChange={(selected) => handleButtonTypeChange([selected])}
                  options={getButtonTypeOptions()}
                  placeholder="Select action"
                  isDisabled={template.category === 'AUTHENTICATION'}
                  className="wa-button-type-select"
                  classNamePrefix="react-select"
                  isSearchable={true}
                />
              </div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Button Text</Form.Label>
              <Form.Control
                type="text"
                value={button.text}
                onChange={(e) => onButtonChange(index, 'text', e.target.value)}
                placeholder="Enter button text"
                maxLength={25}
                className="wa-button-text-input"
              />
              <div className="char-count text-end">{button.text ? button.text.length : 0}/25</div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Country</Form.Label>
              <div className="wa-button-select-wrapper">
                <Select
                  value={countryOptions.find(
                    (option) => option.value === (button.countryCode || '+1')
                  )}
                  onChange={(selected) => {
                    onButtonChange(index, 'countryCode', selected.value);
                  }}
                  options={countryOptions}
                  placeholder="Select country"
                  className="wa-button-type-select"
                  classNamePrefix="react-select"
                  isSearchable={true}
                  formatOptionLabel={(option) => (
                    <div>
                      {option.label} ({getCountryCallingCode(option.code)})
                    </div>
                  )}
                  components={{
                    SingleValue: (props) => (
                      <div className="wa-button-selected-country">
                        {props.data.label} ({getCountryCallingCode(props.data.code)})
                      </div>
                    ),
                  }}
                />
              </div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Phone number</Form.Label>
              <Form.Control
                type="tel"
                value={button.phoneNumber || ''}
                onChange={(e) => {
                  const cleanNumber = validatePhoneNumber(e.target.value);
                  onButtonChange(index, 'phoneNumber', cleanNumber);
                }}
                placeholder="Phone number without country code"
                className="wa-button-phone-input"
                maxLength={20}
              />
              <div className="char-count text-end">
                {button.phoneNumber ? button.phoneNumber.length : 0}/20
              </div>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Button
              variant="danger"
              size="sm"
              className="wa-button-delete-btn"
              onClick={() => onRemoveButton(index)}
            >
              <i className="fas fa-trash"></i>
            </Button>
          </Col>
        </Row>
      );
    } else if (button.type === 'URL') {
      return (
        <Row className="align-items-start">
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Type of Action</Form.Label>
              <div className="wa-button-select-wrapper">
                <Select
                  value={getCurrentButtonType()}
                  onChange={(selected) => handleButtonTypeChange([selected])}
                  options={getButtonTypeOptions()}
                  placeholder="Select action"
                  isDisabled={template.category === 'AUTHENTICATION'}
                  className="wa-button-type-select"
                  classNamePrefix="react-select"
                  isSearchable={true}
                />
              </div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Button Text</Form.Label>
              <Form.Control
                type="text"
                value={button.text}
                onChange={(e) => onButtonChange(index, 'text', e.target.value)}
                placeholder="Enter button text"
                maxLength={25}
                className="wa-button-text-input"
              />
              <div className="char-count text-end">{button.text ? button.text.length : 0}/25</div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">URL Type</Form.Label>
              <div className="wa-button-select-wrapper">
                <Select
                  value={urlTypeOptions.find(
                    (option) => option.value === (button.urlType || 'Static')
                  )}
                  onChange={(selected) => {
                    onButtonChange(index, 'urlType', selected.value);
                  }}
                  options={urlTypeOptions}
                  placeholder="Select URL type"
                  className="wa-button-type-select"
                  classNamePrefix="react-select"
                  isSearchable={true}
                />
              </div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Website URL</Form.Label>
              <Form.Control
                type="url"
                value={button.url || ''}
                onChange={(e) => onButtonChange(index, 'url', e.target.value)}
                placeholder="https://www.example.com"
                className="wa-button-url-input"
                maxLength={2000}
              />
              <div className="char-count text-end">{button.url ? button.url.length : 0}/2000</div>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Button
              variant="danger"
              size="sm"
              className="wa-button-delete-btn"
              onClick={() => onRemoveButton(index)}
            >
              <i className="fas fa-trash"></i>
            </Button>
          </Col>
        </Row>
      );
    } else if (isMarketingOptOutButton()) {
      return (
        <>
          <Row className="align-items-start">
            <Col>
              <Form.Group className="wa-button-form-group">
                <Form.Label className="wa-button-form-label">Type of Action</Form.Label>
                <div className="wa-button-select-wrapper">
                  <Select
                    value={getCurrentButtonType()}
                    onChange={(selected) => handleButtonTypeChange([selected])}
                    options={getButtonTypeOptions()}
                    placeholder="Select action"
                    isDisabled={template.category === 'AUTHENTICATION'}
                    className="wa-button-type-select"
                    classNamePrefix="react-select"
                    isSearchable={true}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="wa-button-form-group">
                <Form.Label className="wa-button-form-label">Button Text</Form.Label>
                <Form.Control
                  type="text"
                  value={button.text}
                  onChange={(e) => onButtonChange(index, 'text', e.target.value)}
                  placeholder="Stop promotions"
                  maxLength={25}
                  className="wa-button-text-input"
                />
                <div className="char-count text-end">{button.text ? button.text.length : 0}/25</div>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="wa-button-form-group">
                <Form.Label className="wa-button-form-label">Footer Text</Form.Label>
                <Form.Control
                  type="text"
                  value={button.footerText || ''}
                  onChange={(e) => onButtonChange(index, 'footerText', e.target.value)}
                  placeholder="Not interested? Tap Stop promotions"
                  className="wa-button-text-input"
                />
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Button
                variant="danger"
                size="sm"
                className="wa-button-delete-btn"
                onClick={() => onRemoveButton(index)}
              >
                <i className="fas fa-trash"></i>
              </Button>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <Form.Check
                type="checkbox"
                id={`opt-out-acknowledgement-${index}`}
                label="I understand that it's responsibility to stop sending marketing messages to customers who opt out."
                checked={button.optOutAcknowledged || false}
                onChange={(e) => onButtonChange(index, 'optOutAcknowledged', e.target.checked)}
                className="wa-opt-out-checkbox"
              />
            </Col>
          </Row>
        </>
      );
    } else if (isCopyOfferCodeButton()) {
      return (
        <Row className="align-items-start">
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Type of Action</Form.Label>
              <div className="wa-button-select-wrapper">
                <Select
                  value={getCurrentButtonType()}
                  onChange={(selected) => handleButtonTypeChange([selected])}
                  options={getButtonTypeOptions()}
                  placeholder="Select action"
                  isDisabled={template.category === 'AUTHENTICATION'}
                  className="wa-button-type-select"
                  classNamePrefix="react-select"
                  isSearchable={true}
                />
              </div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Button Text</Form.Label>
              <Form.Control
                type="text"
                value={button.text}
                onChange={(e) => onButtonChange(index, 'text', e.target.value)}
                placeholder="Copy offer code"
                maxLength={25}
                className="wa-button-text-input"
              />
              <div className="char-count text-end">{button.text ? button.text.length : 0}/25</div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Offer code</Form.Label>
              <Form.Control
                type="text"
                value={button.offerCode || ''}
                onChange={(e) => onButtonChange(index, 'offerCode', e.target.value)}
                placeholder="Enter sample"
                maxLength={15}
                className="wa-button-text-input"
              />
              <div className="char-count text-end">
                {button.offerCode ? button.offerCode.length : 0}/15
              </div>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Button
              variant="danger"
              size="sm"
              className="wa-button-delete-btn"
              onClick={() => onRemoveButton(index)}
            >
              <i className="fas fa-trash"></i>
            </Button>
          </Col>
        </Row>
      );
    } else {
      return (
        <Row className="align-items-start">
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Type of Action</Form.Label>
              <div className="wa-button-select-wrapper">
                <Select
                  value={getCurrentButtonType()}
                  onChange={(selected) => handleButtonTypeChange([selected])}
                  options={getButtonTypeOptions()}
                  placeholder="Select action"
                  isDisabled={template.category === 'AUTHENTICATION'}
                  className="wa-button-type-select"
                  classNamePrefix="react-select"
                  isSearchable={true}
                />
              </div>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="wa-button-form-group">
              <Form.Label className="wa-button-form-label">Button Text</Form.Label>
              <Form.Control
                type="text"
                value={button.text}
                onChange={(e) => onButtonChange(index, 'text', e.target.value)}
                placeholder="Enter button text"
                maxLength={25}
                className="wa-button-text-input"
              />
              <div className="char-count text-end">{button.text ? button.text.length : 0}/25</div>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Button
              variant="danger"
              size="sm"
              className="wa-button-delete-btn"
              onClick={() => onRemoveButton(index)}
            >
              <i className="fas fa-trash"></i>
            </Button>
          </Col>
        </Row>
      );
    }
  };

  return <div className="wa-button-type-control button-form-container">{renderButtonFields()}</div>;
};

// PropTypes for the component
ButtonTypeControl.propTypes = {
  button: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  buttonTypes: PropTypes.array.isRequired,
  template: PropTypes.object.isRequired,
  onButtonChange: PropTypes.func.isRequired,
  onRemoveButton: PropTypes.func.isRequired,
};

export default ButtonTypeControl;
